import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router, NavigationEnd, RouterLink, RouterLinkActive } from '@angular/router';
import { Subject, finalize, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SuperAdminAuthService } from '../core/services/super-admin-auth.service';
import { AppService } from '../service/app.service';
import { DomSanitizer } from '@angular/platform-browser';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MenuModule } from 'headlessui-angular';
import { FormsModule } from '@angular/forms';
import { AsyncPipe, CommonModule } from '@angular/common';
import { SharedModule } from '../shared/shared.module';
import { IconModule } from '../shared/icon/icon.module';
import { AdminSessionProfileService } from '../core/services/admin-session-profile.service';
import { resolvePageTitle } from '../core/utils/page-title.util';
import { SUPER_ADMIN_PERMISSION_SLUGS } from '../core/constants/super-admin-permission-slugs';
import { PermissionService } from '../core/services/permission.service';
import { HasPermissionPipe } from '../core/pipes/has-permission.pipe';
import { PlatformNotificationService } from '../core/services/platform-notification.service';
import { BrandLogoService } from '../core/services/brand-logo.service';

const DEFAULT_LOGO = '/assets/images/logo.svg';

/** Notifications that point at a page (e.g. platform-health alerts) carry it in meta.redirect_url. */
function redirectUrlOf(meta: Record<string, unknown> | null): string | null {
    const url = meta?.['redirect_url'];
    return typeof url === 'string' ? url : null;
}
interface HeaderNotificationView {
    id: number;
    title: string;
    message: string;
    time: string;
    /** Page the notification points at (meta.redirect_url), e.g. /platform-health for health alerts. */
    redirectUrl: string | null;
}

@Component({
    selector: 'header',
    templateUrl: './header.html',
    styleUrls: ['./header.css'],
    standalone: true,
    imports: [
    CommonModule,
    RouterLink,
    FormsModule,
    MenuModule,
    RouterLinkActive,
    TranslateModule,
    SharedModule,
    IconModule,
    AsyncPipe,
    HasPermissionPipe,
],
})
export class HeaderComponent implements OnInit, OnDestroy {
    private readonly appSetting = inject(AppService);
    private readonly platformNotifications = inject(PlatformNotificationService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly brandLogo = inject(BrandLogoService);
    private orgLogoUrl: string | null = null;
    /** Cleared by a successful org-logo load. Presigned urls always differ, so the url itself cannot bound retries. */
    private retryAllowed = true;
    private deadLogoUrl: string | null = null;
    private readonly destroy$ = new Subject<void>();
    private notificationSub?: Subscription;
    private unreadSub?: Subscription;
    readonly permissionService = inject(PermissionService);
    readonly store = this.appSetting.indexState;
    readonly slugs = SUPER_ADMIN_PERMISSION_SLUGS;
    search = false;
    currentPageName: string = 'Dashboard';

    /** Workflows nav dropdown — Global vs Client authoring. */
    workflowNavActive = false;

    /** Expanded state of the "System Preferences" group in the profile dropdown. */
    settingsGroupOpen = false;
    /** AI / LLM Monitoring is its own group: four screens that are read together, not config. */
    llmGroupOpen = false;

    private readonly appTitleSuffix = 'Agentic PD Super Admin';

    /** Max unread items shown in the header bell dropdown (full list is on /notifications). */
    readonly headerNotificationLimit = 5;

    notifications: HeaderNotificationView[] = [];
    unreadCount = 0;

    get headerNotifications(): HeaderNotificationView[] {
        return this.notifications.slice(0, this.headerNotificationLimit);
    }
    messages = [
        {
            id: 1,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-success-light dark:bg-success text-success dark:text-success-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path></svg></span>`
            ),
            title: 'Congratulations!',
            message: 'Your OS has been updated.',
            time: '1hr',
        },
        {
            id: 2,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-info-light dark:bg-info text-info dark:text-info-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg></span>`
            ),
            title: 'Did you know?',
            message: 'You can switch between artboards.',
            time: '2hr',
        },
        {
            id: 3,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-danger-light dark:bg-danger text-danger dark:text-danger-light"> <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg></span>`
            ),
            title: 'Something went wrong!',
            message: 'Send Reposrt',
            time: '2days',
        },
        {
            id: 4,
            image: this.sanitizer.bypassSecurityTrustHtml(
                `<span class="grid place-content-center w-9 h-9 rounded-full bg-warning-light dark:bg-warning text-warning dark:text-warning-light"><svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">    <circle cx="12" cy="12" r="10"></circle>    <line x1="12" y1="8" x2="12" y2="12"></line>    <line x1="12" y1="16" x2="12.01" y2="16"></line></svg></span>`
            ),
            title: 'Warning',
            message: 'Your password strength is low.',
            time: '5days',
        },
    ];

    constructor(
        public translate: TranslateService,
        public storeData: Store<any>,
        public router: Router,
        private readonly sanitizer: DomSanitizer,
        private readonly auth: SuperAdminAuthService,
        readonly sessionProfile: AdminSessionProfileService,
    ) {}

    /** Calls backend to revoke session and clear cookie, then navigates to sign-in. */
    logout(): void {
        this.permissionService.clearPermissions();
        this.auth
            .logout()
            .pipe(
                finalize(() =>
                    void this.router.navigate(['/auth/signin'], { replaceUrl: true }),
                ),
            )
            .subscribe({
                error: () => {
                    /* navigate in finalize even if request fails */
                },
            });
    }
    get usingOrgLogo(): boolean {
        return !!this.orgLogoUrl && this.orgLogoUrl !== this.deadLogoUrl;
    }

    get displayedLogo(): string {
        return this.usingOrgLogo ? (this.orgLogoUrl as string) : DEFAULT_LOGO;
    }

    /** An expired presigned link: show the default at once, ask for a fresh link once, and stop if that fails too. */
    onLogoError(): void {
        if (!this.usingOrgLogo) {
            return;
        }
        this.deadLogoUrl = this.orgLogoUrl;
        if (this.retryAllowed) {
            this.retryAllowed = false;
            this.brandLogo.refresh();
        }
    }

    onOrgLogoLoaded(): void {
        if (this.usingOrgLogo) {
            this.retryAllowed = true;
        }
    }

    ngOnInit() {
        this.brandLogo.logoUrl$.pipe(takeUntil(this.destroy$)).subscribe((url) => {
            this.orgLogoUrl = url;
        });
        this.brandLogo.refresh();
        // Initialize page name on component load
        this.updatePageTitle(this.router.url);

        this.setActiveDropdown();
        this.router.events.subscribe((event) => {
            if (event instanceof NavigationEnd) {
                this.setActiveDropdown();
                this.updatePageTitle(event.urlAfterRedirects);
                this.refreshWorkflowNavActive(event.urlAfterRedirects);
            }
        });

        this.platformNotifications.connectLive();
        this.platformNotifications.loadNotifications(true).subscribe();
        this.notificationSub = this.platformNotifications.notifications.subscribe(
            (items) => {
                this.notifications = items
                    .filter((n) => !n.is_read)
                    .map((n) => ({
                        id: n.id,
                        title: n.title,
                        message: n.message,
                        time: this.formatNotificationTime(n.created_at),
                        redirectUrl: redirectUrlOf(n.meta),
                    }));
            },
        );
        this.unreadSub = this.platformNotifications.unreadCount.subscribe(
            (count) => (this.unreadCount = count),
        );

        this.permissionService.permissionsRevision$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.detectChanges());
        this.permissionService.ensureLoaded()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.cdr.detectChanges());
    }

    toggleSettingsGroup(): void {
        this.settingsGroupOpen = !this.settingsGroupOpen;
    }

    toggleLlmGroup(): void {
        this.llmGroupOpen = !this.llmGroupOpen;
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.notificationSub?.unsubscribe();
        this.unreadSub?.unsubscribe();
    }

  /** System Preferences — roles, stimulate agent, SMTP, email templates, organization. */
  get canAccessUserSettings(): boolean {
    return this.permissionService.canAny([
            this.slugs.ROLE_LIST,
            this.slugs.SIMULATE_AGENT_SETTINGS_LIST,
            this.slugs.SIMULATE_AGENT_SETTINGS_UPDATE,
            this.slugs.SMTP_SETTINGS_LIST,
            this.slugs.SMTP_SETTINGS_UPDATE,
            this.slugs.ORGANIZATION_DETAILS_LIST,
            this.slugs.ORGANIZATION_DETAILS_UPDATE,
            this.slugs.EMAIL_TEMPLATE_LIST,
            this.slugs.EMAIL_TEMPLATE_UPDATE,
            this.slugs.EMAIL_TEMPLATE_TOGGLE,
        ]);
    }

    private formatNotificationTime(iso: string): string {
        const then = new Date(iso).getTime();
        const diffMs = Date.now() - then;
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return 'Just now';
        if (mins < 60) return `${mins} min ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        return `${days}d ago`;
    }

    private updatePageTitle(url: string): void {
        this.currentPageName = resolvePageTitle(url);
        document.title = `${this.currentPageName} | ${this.appTitleSuffix}`;
    }

    private refreshWorkflowNavActive(url: string): void {
        this.workflowNavActive = url.includes('/workflows');
    }

    setActiveDropdown() {
        this.refreshWorkflowNavActive(this.router.url);
        const selector = document.querySelector('ul.horizontal-menu a[routerLink="' + globalThis.location.pathname + '"]');
        if (selector) {
            selector.classList.add('active');
            const all: any = document.querySelectorAll('ul.horizontal-menu .nav-link.active');
            all[0]?.classList.remove('active');
            const ul: any = selector.closest('ul.sub-menu');
            if (ul) {
                let ele: any = ul.closest('li.menu').querySelectorAll('.nav-link');
                if (ele) {
                    ele = ele[0];
                    setTimeout(() => {
                        ele?.classList.add('active');
                    });
                }
            }
        }
    }

    /** Health (and future) notifications carry a page in meta.redirect_url; opening one also marks it read. */
    openNotification(n: HeaderNotificationView): void {
        if (!n.redirectUrl) return;
        this.platformNotifications.markRead(n.id).subscribe();
        void this.router.navigateByUrl(n.redirectUrl);
    }

    removeNotification(value: number) {
        this.platformNotifications.markRead(value).subscribe();
    }

    markAllNotificationsRead(): void {
        this.platformNotifications.markAllRead().subscribe();
    }

    removeMessage(value: number) {
        this.messages = this.messages.filter((d) => d.id !== value);
    }

    changeLanguage(item: any) {
        this.translate.use(item.code);
        this.appSetting.toggleLanguage(item);
        if (this.store()?.locale?.toLowerCase() === 'ae') {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'rtl'});
        } else {
            this.storeData.dispatch({type: 'toggleRTL', payload: 'ltr'});
        }
        globalThis.location.reload();
    }

}
